// src/hooks/useEmployeeExport.ts
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

const EMPLOYEE_BASE_URL = API_BASE_PATH;

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// Helper for auth header
const getAuthHeader = (_token?: string | null) =>
  _token ? { Authorization: `Bearer ${_token}` } : {};

// 1. Export All Employees
export const exportAllEmployeesExcel = async (_token?: string | null) => {
  try {
    const response = await axios.get(
      `${EMPLOYEE_BASE_URL}/register/employees/export/excel`,
      {
        headers: {
          accept: "application/json",
          ...getAuthHeader(_token),
        },
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    const hours = now.getHours() % 12 || 12;
    const ampm = now.getHours() >= 12 ? "pm" : "am";

    const filename = `employees${pad(now.getDate())}${pad(
      now.getMonth() + 1
    )}${now.getFullYear()}_${pad(hours)}${pad(now.getMinutes())}${ampm}.xlsx`;

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    toast.success(`Employee Excel downloaded: ${filename}`);
  } catch (error) {
    console.error("Export all employees failed:", error);
    toast.error(extractErrorMessage(error));
  }
};

// 2. Export Selected Employees by IDs
export const exportSelectedEmployeesExcel = async (
  employeeIds: string[],
  _token?: string | null
) => {
  try {
    if (!employeeIds.length) {
      toast.warning("No employee selected to export.");
      return;
    }

    const query = employeeIds.map((id) => `employee_id=${id}`).join("&");

    const response = await axios.get(
      `${EMPLOYEE_BASE_URL}/register/export/excel?${query}`,
      {
        headers: {
          accept: "application/json",
          ...getAuthHeader(_token),
        },
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    const hours = now.getHours() % 12 || 12;
    const ampm = now.getHours() >= 12 ? "pm" : "am";

    const filename = `employees${pad(now.getDate())}${pad(
      now.getMonth() + 1
    )}${now.getFullYear()}_${pad(hours)}${pad(now.getMinutes())}${ampm}.xlsx`;

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    toast.success(`Selected Employees Excel downloaded: ${filename}`);
  } catch (error) {
    console.error("Export selected employees failed:", error);
    toast.error(extractErrorMessage(error));
  }
};

// 3. Export All Employees with Custom Columns
export const exportCustomColumnsEmployees = async (
  selectedColumns: string[],
  _token?: string | null
) => {
  try {
    if (!selectedColumns.length) {
      toast.warning("Please select at least one column to export.");
      return;
    }

    const query = selectedColumns
      .map((col) => `selected_columns=${col}`)
      .join("&");

    const response = await axios.post(
      `${EMPLOYEE_BASE_URL}/register/download-selected-employee?${query}`,
      {},
      {
        headers: {
          accept: "application/json",
          ...getAuthHeader(_token),
        },
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    const hours = now.getHours() % 12 || 12;
    const ampm = now.getHours() >= 12 ? "pm" : "am";

    const filename = `employees_update${pad(now.getDate())}${pad(
      now.getMonth() + 1
    )}${now.getFullYear()}_${pad(hours)}${pad(now.getMinutes())}${ampm}.xlsx`;

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    toast.success(`Custom Columns Excel downloaded: ${filename}`);
  } catch (error) {
    console.error("Export custom columns failed:", error);
    toast.error(extractErrorMessage(error));
  }
};

// 4. Import Excel and Update Existing Employees
export const importEmployeeUpdateExcel = async (
  file: File,
  _token?: string | null
) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    await axios.put(
      `${EMPLOYEE_BASE_URL}/register/import-excel-update/`,
      formData,
      {
        headers: {
          accept: "application/json",
          "Content-Type": "multipart/form-data",
          ...getAuthHeader(_token),
        },
      }
    );

    toast.success("Employee data updated successfully from Excel.");
  } catch (error) {
    console.error("Employee update import failed:", error);
    toast.error(extractErrorMessage(error));
  }
};

// 5. Import Excel and Add New Employees
export const importEmployeePostExcel = async (
  file: File,
  _token?: string | null
) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    await axios.post(
      `${EMPLOYEE_BASE_URL}/register/import-excel-post/`,
      formData,
      {
        headers: {
          accept: "application/json",
          "Content-Type": "multipart/form-data",
          ...getAuthHeader(_token),
        },
      }
    );

    toast.success("New employees imported successfully from Excel.");
  } catch (error) {
    console.error("Employee post import failed:", error);
    toast.error(extractErrorMessage(error));
  }
};
